import { type ExternalUserServiceIntegration } from '../../../../models/User'
import { type UserIntegrationIssue } from '../../../../store/user/user'
import { IntegrationServiceId } from '../../../../common/integrations'
import { CalendarProvider, MeetingPlatformProvider, type UserIntegrations } from '../../../../models/Scheduling'

export interface CalendarOption {
  serviceId: IntegrationServiceId
  provider: CalendarProvider
  label: string
}

export const GOOGLE_CALENDAR_OPTION: CalendarOption = {
  serviceId: IntegrationServiceId.GOOGLE_CALENDAR,
  provider: CalendarProvider.GOOGLE,
  label: 'Google Calendar',
}

export const OUTLOOK_CALENDAR_OPTION: CalendarOption = {
  serviceId: IntegrationServiceId.OUTLOOK_CALENDAR,
  provider: CalendarProvider.OUTLOOK,
  label: 'Outlook Calendar',
}

export const CALENDAR_OPTIONS: Array<CalendarOption> = [GOOGLE_CALENDAR_OPTION, OUTLOOK_CALENDAR_OPTION]

export enum IntegrationConnectionStatus {
  READY = 'ready',
  NEEDS_RECONNECT = 'needs_reconnect',
  NOT_CONNECTED = 'not_connected',
}

export interface IntegrationConnection {
  status: IntegrationConnectionStatus
  email?: string
}

export const getIntegrationConnection = (
  integrations: Array<ExternalUserServiceIntegration> | undefined,
  problems: Array<UserIntegrationIssue> | undefined,
  serviceId: IntegrationServiceId,
  /**
   * Determined by scheduling-be whether the user-integration has the required
   * scopes and permissions needed for scheduling to function properly
   */
  capable?: boolean,
): IntegrationConnection => {
  const integration = (integrations ?? []).find((candidate) => candidate.serviceId === serviceId)
  if (!integration) {
    return { status: IntegrationConnectionStatus.NOT_CONNECTED }
  }
  const hasProblem = (problems ?? []).some((problem) => problem.integrationId === integration.id)
  return {
    status: integration.state === 'live' && !hasProblem && capable !== false ? IntegrationConnectionStatus.READY : IntegrationConnectionStatus.NEEDS_RECONNECT,
    email: integration.email ?? (integration.type === 'calendar' ? integration.externalAccountId : undefined),
  }
}

export const getSchedulingCapability = (integrations: UserIntegrations, provider: CalendarProvider | MeetingPlatformProvider): boolean | undefined =>
  [...integrations.calendars, ...integrations.meetingPlatforms].find((integration) => integration.provider === provider)?.capable

export const NATIVE_PLATFORM_BY_CALENDAR: Partial<Record<CalendarProvider, MeetingPlatformProvider>> = {
  [CalendarProvider.GOOGLE]: MeetingPlatformProvider.GOOGLE_MEET,
  [CalendarProvider.OUTLOOK]: MeetingPlatformProvider.MS_TEAMS,
}

export const isPlatformCompatibleWithCalendar = (platform: MeetingPlatformProvider, calendar: CalendarProvider): boolean =>
  platform === MeetingPlatformProvider.ZOOM || NATIVE_PLATFORM_BY_CALENDAR[calendar] === platform

export const resolveIntegrationsOnCalendarSelect = (current: UserIntegrations, calendar: CalendarProvider, isZoomSelectable: boolean): UserIntegrations => {
  const keepZoom = current.selectedMeetingPlatform === MeetingPlatformProvider.ZOOM && isZoomSelectable
  return {
    ...current,
    selectedCalendar: calendar,
    selectedMeetingPlatform: keepZoom ? MeetingPlatformProvider.ZOOM : (NATIVE_PLATFORM_BY_CALENDAR[calendar] ?? current.selectedMeetingPlatform),
  }
}
